import React, { useEffect, useState } from "react";
import { Modal, ModalContent, cn, getTypeBadge } from "@racoon/ui";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useT, Namespace } from "@racoon/i18n";

interface ScaleRecordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scaleRecord: any;
  formatTimeRoute: (dateStr?: string | Date) => string;
  formatDate: (dateStr?: string | Date) => string;
}

export const ScaleRecordModal: React.FC<ScaleRecordModalProps> = ({
  open,
  onOpenChange,
  scaleRecord,
  formatTimeRoute,
  formatDate,
}) => {
  const t = useT();
  const [currentIndex, setCurrentIndex] = useState(0);
  const photos = scaleRecord?.photos || [];

  useEffect(() => {
    setCurrentIndex(0);
  }, [scaleRecord?.id]);

  const handleNext = () => {
    if (photos.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const handlePrev = () => {
    if (photos.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  if (!scaleRecord) return null;

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="max-w-[500px] p-0 overflow-hidden bg-white rounded-[20px] shadow-2xl border-0">
        <div className="p-4 flex flex-row items-center justify-between border-b border-gray-100">
          <h2 className="text-[20px] font-bold text-[#1D1F23] leading-[24px] tracking-[-0.1px] m-0">
            {t(Namespace.Routes, "weightRecord")}{" "}
            <span className="text-[#0066FF]">#{scaleRecord.id}</span>
          </h2>
        </div>

        <div className="px-4 ">
          <div className="relative w-full h-[310px] bg-gray-100 flex items-center justify-center rounded-[16px] overflow-hidden">
            {photos.length > 0 ? (
              <>
                <img
                  src={photos[currentIndex]}
                  alt={`${t(Namespace.Routes, "weightRecordPhoto")} ${currentIndex + 1}`}
                  className="w-full h-full object-cover cursor-pointer transition-opacity hover:opacity-90"
                  onDoubleClick={() =>
                    window.open(photos[currentIndex], "_blank", "noopener,noreferrer")
                  }
                  title="Double click to open"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      window.open(photos[currentIndex], "_blank", "noopener,noreferrer");
                    }
                  }}
                />

                {photos.length > 1 && (
                  <>
                    <button
                      type="button"
                      aria-label="Previous photo"
                      onClick={handlePrev}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-10 bg-white rounded-[8px] flex items-center justify-center shadow-md hover:bg-gray-50 transition-colors"
                    >
                      <ChevronLeft size={20} className="text-gray-900" strokeWidth={2} />
                    </button>
                    <button
                      type="button"
                      aria-label="Next photo"
                      onClick={handleNext}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-10 bg-white rounded-[8px] flex items-center justify-center shadow-md hover:bg-gray-50 transition-colors"
                    >
                      <ChevronRight size={20} className="text-gray-900" strokeWidth={2} />
                    </button>

                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10 pointer-events-none">
                      {photos.map((_: string, i: number) => (
                        <div
                          key={i}
                          className={cn(
                            "w-1.5 h-1.5 rounded-full transition-colors shadow-sm",
                            i === currentIndex ? "bg-[#0066FF]" : "bg-white",
                          )}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="text-gray-400 text-sm">{t(Namespace.Routes, "noPhoto")}</div>
            )}
          </div>
        </div>

        <div className="px-4 pb-4  bg-white flex flex-col gap-4 text-[15px] text-[#1D1F23]">
          <div className="flex justify-between items-center">
            <span>{t(Namespace.Routes, "recordNumber")}</span>
            <span className="border-b border-[#1D1F23] pb-[1px]">#{scaleRecord.id}</span>
          </div>

          <div className="flex justify-between items-center">
            <span>{t(Namespace.Routes, "dateTime")}</span>
            <span className="border-b border-[#1D1F23] pb-[1px]">
              {formatDate(scaleRecord.brutto_scale_ts || scaleRecord.netto_scale_ts)}{" "}
              {formatTimeRoute(scaleRecord.brutto_scale_ts || scaleRecord.netto_scale_ts)}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span>{t(Namespace.Routes, "netWeight")}</span>
            <span className="border-b border-[#1D1F23] pb-[1px]">
              {scaleRecord.netto_weight_kg ?? "---"} кг
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span>{t(Namespace.Routes, "volume")}</span>
            <span className="border-b border-[#1D1F23] pb-[1px]">
              {scaleRecord.volume ?? "---"} м3
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span>{t(Namespace.Routes, "wasteType")}</span>
            <span className="border-b border-[#1D1F23] pb-[1px]">
              {getTypeBadge(scaleRecord.waste_type, t)}
            </span>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};
